import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { TimeUnit, TooltipItem } from "chart.js";
import { useDependency } from "../../../../contexts/DependencyContext";
import { DateService } from "../../../../services/types/DateService";
import { DataSourceService } from "../../../../services/http/datasource/DataSourceService";
import { IDataSource } from "../../../../services/http/datasource/models/IDataSource";
import { MeasureService } from "../../../../services/http/measure/MeasureService";
import { INumericTimeSeriesPoints } from "../../../../services/http/measure/models/INumericTimeSeriesPoints";
import { GetDataSourceMeasureHistoryByPointsParameters } from "../../../../services/http/measure/models/GetDataSourceMeasureHistoryByPointsParameters";
import { ISensorChartConfiguration } from "../../../../services/http/widget/models/ISensorChartConfiguration";
import { IWidget } from "../../../../services/http/widget/models/IWidget";
import IndicatorNumber from "../../../../components/Shared/IndicatorNumber";
import LineChart, { ILineChartSeries, ChartPointsPerPixelRatio } from "../../../../components/Shared/LineChart";
import Spinner from "../../../../components/Shared/Spinner";
import { IDateRange } from "../DateFilters";
import styles from './SensorChartFullScreen.module.scss';

interface ISensorChartProps
{
    widget: IWidget;
    dateFilters: IDateRange[];
}

interface ISensorChartContent
{
    dataSource: IDataSource;
    timeSeriesPoints: INumericTimeSeriesPoints[];
}

function getTimeUnit(from: Date, to: Date): TimeUnit
{
    const ms = to.getTime() - from.getTime();
    const sec = ms / 1000;
    const minute = sec / 60;
    const hour = minute / 60;
    const day = hour / 24;

    if (ms < 100)
        return 'millisecond';

    if (sec < 100)
        return 'second';

    if (minute < 100)
        return 'minute';

    if (hour < 100)
        return 'hour';

    if (day < 100)
        return 'day';

    return 'week';
}

async function loadData(dataSourceService: DataSourceService, measureService: MeasureService, dataSourceId: number, dateFilters: IDateRange[], maxPoints: number): Promise<ISensorChartContent>
{
    const dataSource = await dataSourceService.getById(dataSourceId);
    const timeSeriesPoints: INumericTimeSeriesPoints[] = [];

    for (let i = 0; i < dateFilters.length; i++)
    {
        const dateFilter = dateFilters[i];
        const fromDate = dateFilter.from;
        const toDate = dateFilter.to;

        if (fromDate === null || toDate === null || maxPoints <= 1)
            continue;

        const parameters = new GetDataSourceMeasureHistoryByPointsParameters();
        parameters.dataSourceId = dataSourceId;
        parameters.fromDate = new Date(fromDate);
        parameters.toDate = new Date(toDate);
        parameters.maxPoints = maxPoints;

        timeSeriesPoints.push(await measureService.getNumericTimeSeriesPoints(parameters));
    }

    return { dataSource, timeSeriesPoints };
}

function SensorChartFullScreen(props: ISensorChartProps): ReactElement
{
    const dataSourceService = useDependency(DataSourceService);
    const measureService = useDependency(MeasureService);
    const dateService = useDependency(DateService);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const lastDateFilters = useRef<IDateRange[]>([]);
    const sensorChartContent = useRef<ISensorChartContent | null>(null);
    const [chartSeries, setChartSeries] = useState<ILineChartSeries[]>([]);
    const configuration = JSON.parse(props.widget.uiConfiguration) as ISensorChartConfiguration;
    const maxPoints = chartContainerRef.current ? Math.round(chartContainerRef.current.offsetWidth * ChartPointsPerPixelRatio) : 1;
    const timeUnit = props.dateFilters &&
        props.dateFilters.length >= 1 &&
        props.dateFilters[0].from &&
        props.dateFilters[0].to
        ? getTimeUnit(props.dateFilters[0].from, props.dateFilters[0].to)
        : 'day';

    function getChartLabel(): string
    {
        const dataSource = sensorChartContent.current?.dataSource;

        return dataSource
            ? `${dataSource.unitOfMeasureName} ${(dataSource.unitOfMeasureId !== 41
                ? `(${dataSource.unitOfMeasureCode})`
                : dataSource.unitOfMeasureCode)}`
            : '';
    }

    const getChartSeries = useCallback((): ILineChartSeries[] =>
    {
        function mapTimeSeries(timeSeries: INumericTimeSeriesPoints)
        {
            const { dateFrom, dateTo, values } = timeSeries;
            const range = dateService.getDateRange(dateFrom, dateTo, values.length);
            const title = `Sensor ${dateService.format(new Date(dateFrom), 'M/d/yy HH:mm')} - ${dateService.format(new Date(dateTo), 'M/d/yy HH:mm')}`;
            (timeSeries as any)['dateRange'] = range;

            return {
                title,
                labels: range.map(y => y.toJSON()),
                values: values.map(y => y !== null ? y : null)
            } as ILineChartSeries;
        }

        return (sensorChartContent.current?.timeSeriesPoints)
            ? sensorChartContent.current.timeSeriesPoints.map(x => mapTimeSeries(x))
            : [];
    }, [dateService]);

    const getTooltipLabel = useCallback((tooltipItem: TooltipItem<"line">): string =>
    {
        const series = sensorChartContent.current?.timeSeriesPoints;

        if (!series || series.length === 0 || !tooltipItem || tooltipItem.datasetIndex === undefined || tooltipItem.dataIndex === undefined)
            return '';

        const range = (series[tooltipItem.datasetIndex] as any)['dateRange'];
        const fixedRawValue = Number(tooltipItem.raw)?.toFixed(5);
        const yValue = parseFloat(fixedRawValue);
        const xValue = dateService.format(new Date(range[tooltipItem.dataIndex]), 'MM/dd/yyyy HH:mm:ss');

        return `${yValue.toString()} - ${xValue}`;
    }, [dateService])

    const getTooltipTitle = useCallback((_: any[]): string => "", []);

    useEffect(() =>
    {
        async function loadDataAsync(filters: IDateRange[]): Promise<void>
        {
            setChartSeries([]);
            sensorChartContent.current = null;
            sensorChartContent.current = await loadData(dataSourceService, measureService, configuration.dataSourceId, filters, maxPoints);
            if (sensorChartContent.current)
            {
                lastDateFilters.current = filters;
                setChartSeries(getChartSeries());
            }
        }

        if (props.dateFilters.length > 0 && JSON.stringify(lastDateFilters.current) !== JSON.stringify(props.dateFilters))
        {
            loadDataAsync(props.dateFilters);
        }
    }, [props.dateFilters, getChartSeries, configuration.dataSourceId, maxPoints, dataSourceService, measureService])


    const content = sensorChartContent.current;
    const measureHistory = content && content.timeSeriesPoints.length > 0 ? content.timeSeriesPoints[0] : null;
    const indicators = [
        {
            label: "Average",
            unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
            value: measureHistory?.aggregation.average
        },
        {
            label: "Min value",
            unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
            value: measureHistory?.aggregation.minimum
        },
        {
            label: "Max value",
            unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
            value: measureHistory?.aggregation.maximum
        },
        {
            label: "Standard Deviation",
            unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
            value: measureHistory?.aggregation.standardDeviation
        }
    ];

    return (
        <div className={styles.wrapper}>
            <div ref={chartContainerRef}>
                {chartSeries.length === 0 && <Spinner caption="Loading data..." />}
                {chartSeries.length > 0 && <LineChart
                    series={chartSeries}
                    yLabel={getChartLabel()}
                    getTooltipLabel={getTooltipLabel}
                    getTooltipTitle={getTooltipTitle}
                    timeUnit={timeUnit}
                    size="medium"
                    displayLegend={measureHistory?.values !== undefined &&  measureHistory?.values.length > 0 }
                    enableZoom
                />}
            </div>

            <div className={styles.indicators}>
                {indicators.map((indicator, index) =>
                {
                    return typeof indicator.value === "number" && indicator.unitOfMeasure !== undefined ? (
                        <IndicatorNumber
                            key={index}
                            label={indicator.label}
                            unitOfMeasure={indicator.unitOfMeasure}
                            value={indicator.value}
                            decimalPlaces={2}
                        />
                    ) : null
                })}
            </div>
        </div>
    );
}

export default SensorChartFullScreen;

